import { Component, ViewChild, ElementRef, AfterViewInit, OnDestroy, Renderer2, OnInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate, state, keyframes } from '@angular/animations';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators, ValidatorFn, ValidationErrors } from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';
import { ProjectService } from '../Services/project.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedService } from '../Services/sharedService';
import { LoginService } from '../Services/login.service';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { filter, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { VerificationCodeComponent } from '../verification-code/verification-code.component';
import { passwordValidator } from '../password-validators';
import { AuthService } from '../Services/auth.service';
import Swal from 'sweetalert2';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule, MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule, MatProgressSpinner } from '@angular/material/progress-spinner';
import { GoogleSigninComponent } from '../components/google-signin/google-signin.component';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { TokenEncryptionService } from '../Services/token-encryption.service';
import { PasswordSecurityService } from '../Services/password-security.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { captureAttributionTouch, getAnalyticsAttribsWithTouches, getStoredAttributionPayload } from '../utils/attribution.util';
import { trackClarityEvent, setClarityTag } from '../utils/clarity-event.util';
import { Icp, resolveIcpForVisit } from '../utils/icp.util';
import { BrandCopy, getBrandCopy } from '../components/auth-brand-panel/brand-copy';
import { trackProductEvent } from '../utils/product-analytics.util';
import { disposableEmailValidator } from '../utils/disposable-email.util';

interface Testimonial {
  quote: string;
  author: string;
}

@Component({
    selector: 'app-signup',
    templateUrl: './signup.component.html',
    styleUrls: ['./signup.component.scss'],
    animations: [
        trigger('benefitAnimation', [
            state('in', style({ opacity: 1, transform: 'translateY(0)', color: '#FFC83D' })),
            state('out', style({ opacity: 0, transform: 'translateY(15px)' })),
            transition('in => out', [
                animate('0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)')
            ]),
            transition('out => in', [
                animate('0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)')
            ])
        ]),
        trigger('counterAnimation', [
            transition('* => *', [
                animate('0.4s ease-out', keyframes([
                    style({ opacity: 0.8, color: '#FFC83D', offset: 0.2 }),
                    style({ opacity: 1, color: '#FFFFFF', offset: 1.0 })
                ]))
            ])
        ]),
        trigger('slideInOut', [
            transition(':increment', [
                style({ transform: 'translateX(100%)', opacity: 0 }),
                animate('400ms ease-out', style({ transform: 'translateX(0%)', opacity: 1 }))
            ]),
            transition(':decrement', [
                style({ transform: 'translateX(-100%)', opacity: 0 }),
                animate('400ms ease-out', style({ transform: 'translateX(0%)', opacity: 1 }))
            ])
        ])
    ],
    standalone: false
})
export class SignupComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('heroVideo') heroVideo: ElementRef<HTMLVideoElement>;
  @ViewChild('backgroundVideo') backgroundVideoRef!: ElementRef<HTMLVideoElement>;
  private videoTimeUpdateListener!: () => void; // Store listener for removal

  // Start typing straight away on desktop; on phones the keyboard would cover the page
  @ViewChild('emailField') set emailField(ref: ElementRef<HTMLInputElement> | undefined) {
    if (ref && window.matchMedia('(min-width: 961px) and (pointer: fine)').matches) {
      setTimeout(() => ref.nativeElement.focus());
    }
  }

  // Typing a password inserts the strength meter, which refreshes this query.
  // Focus the name field only the first time it appears, not on every refresh.
  private nameFieldAutofocused = false;

  @ViewChild('nameField') set nameField(ref: ElementRef<HTMLInputElement> | undefined) {
    if (!ref) {
      this.nameFieldAutofocused = false;
      return;
    }
    if (this.nameFieldAutofocused) {
      return;
    }
    this.nameFieldAutofocused = true;
    setTimeout(() => ref.nativeElement.focus());
  }
  private routerSubscription: Subscription; // Added routerSubscription

  signupForm: UntypedFormGroup;
  step: number = 1;
  passwordVisible = false;
  passwordStrength: 'weak' | 'medium' | 'strong' | 'very-strong' = 'weak';
  passwordFeedback: string[] = [];
  passwordValidationErrors: string[] = [];
  passwordRequirements: Array<{ text: string; met: boolean }> = [];
  unmetPasswordRequirements: Array<{ text: string; met: boolean }> = [];
  
  // Password breach status
  isPasswordBreached: boolean = false;
  isCheckingBreach: boolean = false;
  private breachCheckTimeout: any = null;
  
  // Cached computed values (updated in onPasswordChange)
  unmetRequirementsCount: number = 0;
  metRequirementsCount: number = 0;
  requirementsProgress: number = 0;
  hasUnmetRequirements: boolean = false;
  hasMinLength: boolean = false;
  
  // Pre-compiled regex patterns for better performance
  private readonly regexPatterns = {
    uppercase: /[A-Z]/,
    lowercase: /[a-z]/,
    number: /\d/,
    specialChar: /[!"#$%&'()*+,\-./:;<=>?@[\]^_{}|~]/,
    backslash: /\\/,
    leadingTrailingSpace: /^\s|\s$/
  };
  
  // Static requirements definition (text only, checks are done dynamically)
  private readonly requirementDefinitions = [
    'Use at least 8 characters',
    'Use no more than 20 characters',
    'Add uppercase letters',
    'Add lowercase letters',
    'Add numbers',
    'Add special characters (not except backslash)',
    'No leading or trailing spaces'
  ];

  loading = false;
  hide = true;
  formSubmitted = false;
  submitAttempted = false; // set only when the user presses the final submit button
  icp: Icp = 'default'; // which landing page the visitor came from, so the left panel can echo its headline

  get brandCopy(): BrandCopy {
    return getBrandCopy(this.icp);
  }
  visible = true;
  isDisable = false;
  isGoogleSignIn = false;
  featureImages = [
    { src: '../../../assets/images/MostUsedApplication.webp', className: 'most-used-application', classNameTop: 'flex_item_third_1' },
    { src: '../../../assets/images/workingHours.webp', className: 'working-hours', classNameTop: 'flex_item_third_2' },
    { src: '../../../assets/images/captureScreenShots.webp', className: 'capture-screenShots', classNameTop: 'flex_item_third_3' }
  ];

  // --- Real-time Counter --- 
  baseUptimeHours: number = 78000; // Base value
  currentUptimeHours: number = 0; // Will be set in ngOnInit with randomization
  private uptimeIntervalId: any = null; // To store interval ID
  animatedHoursValue: string = '';
  // -------------------------

  // Rotating Benefit Logic - Enhanced with Angular animations
  rotatingBenefits: string[] = [
    'tracks productivity, not people',
    'shows live screens—only when you need it',
    'auto-detects idle time and focus dips',
    'improves team output—ethically and clearly'
  ];
  currentBenefitIndex: number = 0;
  currentBenefit: string = this.rotatingBenefits[0];
  benefitAnimationState: string = 'in';

  // Testimonial Rotation Logic
  testimonials: Testimonial[] = [
    { quote: "We improved productivity by 35% after using SuperSee—without pressuring employees.", author: "Jenil Kataria, CEO, VK Sales" },
    { quote: "SuperSee helped us reduce break-time misuse and ensured clear timelines.", author: "Jitender Singh, Blue Bird Overseas" },
    { quote: "The live screen feature is a game-changer for remote visibility. Easy to use and powerful.", author: "Shrushti Desai, QuantumShift Tech" },
    { quote: "Our team focus improved by 20%. SuperSee gave us exactly what we needed.", author: "Jane D., Project Manager" }
  ];
  currentTestimonialIndex: number = 0;
  currentTestimonial: Testimonial = this.testimonials[0];
  isTestimonialVisible: boolean = false; // Start hidden for initial fade
  testimonialInterval: any;

  constructor(
    private renderer: Renderer2,
    private router: Router,
    private _projectService: ProjectService,
    private _snackBar: MatSnackBar,
    private loginService: LoginService,
    private sharedService: SharedService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private tokenEncryptionService: TokenEncryptionService,
    private passwordSecurity: PasswordSecurityService,
    private recaptchaV3Service: ReCaptchaV3Service
  ) {
    this.createSignupForm(); // Ensure this is called if it was missing
    this.sharedService.sideMenuSubject.next(false);
    this.sharedService.logInStateSubject.subscribe((result) => {
      if (result) {
        this.router.navigate(["/"]);
      }
    });

    // Subscribe to router events to reset form on navigation to this component
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Check if the current URL is for the signup page.
      // This ensures reset only when navigating TO signup.
      if (this.router.url.includes('/signup')) {
        if (this.signupForm) {
          this.signupForm.reset({
            fullName: '',
            email: '',
            password: '',
            termsAccepted: false
          });
          this.signupForm.markAsPristine();
          this.signupForm.markAsUntouched();
          // It's important to reset form submission status as well
          this.formSubmitted = false;
          this.submitAttempted = false;
          this.step = 1; // Reset step on navigation
          // Reset password strength indicators
          this.passwordStrength = 'weak';
          this.passwordFeedback = [];
          this.passwordValidationErrors = [];
          this.passwordRequirements = [];
          this.unmetPasswordRequirements = [];
          this.unmetRequirementsCount = 0;
          this.metRequirementsCount = 0;
          this.requirementsProgress = 0;
          this.hasUnmetRequirements = false;
          this.hasMinLength = false;
          this.passwordVisible = false; // Reset password visibility
          this.isPasswordBreached = false;
          this.isCheckingBreach = false;
          if (this.breachCheckTimeout) {
            clearTimeout(this.breachCheckTimeout);
            this.breachCheckTimeout = null;
          }
        } else {
          // Fallback if form isn't initialized, though constructor should handle this.
          this.createSignupForm();
        }
      }
    });
  }

  createSignupForm() { // Added definition for createSignupForm if it was missing or to ensure it exists
    this.signupForm = new UntypedFormGroup({
      fullName: new UntypedFormControl("", [Validators.required, Validators.pattern(/^[a-zA-Z]{2,}(?: [a-zA-Z]{2,})+$/)]),
      email: new UntypedFormControl("", [
        Validators.required,
        Validators.pattern(this.emailPattern),
        disposableEmailValidator(),
      ]),
      password: new UntypedFormControl("", [Validators.required, passwordValidator()]),
      termsAccepted: new UntypedFormControl(false, [Validators.requiredTrue])
      // Add other form controls as needed from your original signup form
    });

    // Listen to password changes for strength checking with debounce for performance
    this.signupForm.get('password')?.valueChanges.pipe(
      debounceTime(100), // Debounce to reduce calculations
      distinctUntilChanged() // Only process if value actually changed
    ).subscribe(() => {
      this.onPasswordChange();
    });
  }

  emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;

  onPasswordChange() {
    const password = this.signupForm.get('password')?.value || '';
    const passwordLength = password.length;
    
    // Pre-compute regex checks once (reuse for multiple requirements)
    const hasUppercase = this.regexPatterns.uppercase.test(password);
    const hasLowercase = this.regexPatterns.lowercase.test(password);
    const hasNumber = this.regexPatterns.number.test(password);
    const hasSpecialChar = this.regexPatterns.specialChar.test(password);
    const hasBackslash = this.regexPatterns.backslash.test(password);
    const hasLeadingTrailingSpace = this.regexPatterns.leadingTrailingSpace.test(password);
    
    // Check each requirement efficiently
    this.hasMinLength = passwordLength >= 8;
    
    this.passwordRequirements = [
      { text: this.requirementDefinitions[0], met: this.hasMinLength },
      { text: this.requirementDefinitions[1], met: passwordLength <= 20 },
      { text: this.requirementDefinitions[2], met: hasUppercase },
      { text: this.requirementDefinitions[3], met: hasLowercase },
      { text: this.requirementDefinitions[4], met: hasNumber },
      { text: this.requirementDefinitions[5], met: hasSpecialChar && !hasBackslash },
      { text: this.requirementDefinitions[6], met: !hasLeadingTrailingSpace }
    ];
    
    // Calculate cached values and filter unmet requirements
    this.metRequirementsCount = 0;
    this.unmetRequirementsCount = 0;
    this.unmetPasswordRequirements = [];
    const unmetRequirements: string[] = [];
    
    for (const req of this.passwordRequirements) {
      if (req.met) {
        this.metRequirementsCount++;
      } else {
        this.unmetRequirementsCount++;
        this.unmetPasswordRequirements.push(req);
        unmetRequirements.push(req.text);
      }
    }
    
    this.requirementsProgress = this.passwordRequirements.length > 0 
      ? (this.metRequirementsCount / this.passwordRequirements.length * 100) 
      : 0;
    this.hasUnmetRequirements = this.unmetRequirementsCount > 0;
    
    // Update strength and feedback
    if (password) {
      const result = this.passwordSecurity.checkPasswordStrength(password);
      this.passwordStrength = result.strength;
      this.passwordFeedback = unmetRequirements;
    } else {
      this.passwordStrength = 'weak';
      this.passwordFeedback = unmetRequirements;
    }
    
    // Clear validation errors array (we're using requirements now)
    this.passwordValidationErrors = [];
    
    // Check password breach with debouncing (only if password has minimum length)
    if (password && password.length >= 8) {
      // Clear existing timeout
      if (this.breachCheckTimeout) {
        clearTimeout(this.breachCheckTimeout);
      }
      
      // Reset breach status while checking
      this.isPasswordBreached = false;
      this.isCheckingBreach = true;
      
      // Debounce breach check to avoid too many API calls (wait 1 second after user stops typing)
      this.breachCheckTimeout = setTimeout(async () => {
        try {
          const isBreached = await this.passwordSecurity.checkPasswordBreach(password);
          this.isPasswordBreached = isBreached;
          this.isCheckingBreach = false;
          this.cdRef.detectChanges();
        } catch (error) {
          // If check fails, don't block the user (fail open)
          this.isPasswordBreached = false;
          this.isCheckingBreach = false;
          this.cdRef.detectChanges();
        }
      }, 1000);
    } else {
      // Reset breach status if password is too short
      this.isPasswordBreached = false;
      this.isCheckingBreach = false;
      if (this.breachCheckTimeout) {
        clearTimeout(this.breachCheckTimeout);
        this.breachCheckTimeout = null;
      }
    }
  }

  signUpAction() {
    this.formSubmitted = true;
    this.submitAttempted = true;

    // Mark all fields as touched to show errors
    Object.values(this.signupForm.controls).forEach(control => {
      control.markAsTouched();
    });

    if (this.signupForm.valid) {
      // Check if password is breached (already checked in real-time)
      if (this.isPasswordBreached) {
        // Show confirmation dialog before allowing signup with breached password
        Swal.fire({
          title: "This password isn't safe",
          html: `<p>This password has been found in a known data breach.</p><div class="ss-impact"><strong>What to do</strong>Choose a new, unique password. You can still create the account with this one.</div>`,
          showCancelButton: true,
          confirmButtonText: 'Continue with this password',
          cancelButtonText: 'Choose a different password',
          confirmButtonColor: '#FFC83D',
          cancelButtonColor: '#6c757d',
          reverseButtons: true,
          focusCancel: true
        }).then((result) => {
          if (result.isConfirmed) {
            // User confirmed, proceed with signup
            this.proceedWithSignup();
          }
          // If cancelled, do nothing (user can change password)
        });
        return;
      }

      // Check password strength
      const password = this.signupForm.get('password')?.value;
      const strength = this.passwordSecurity.checkPasswordStrength(password);
      if (strength.strength === 'weak') {
        this._snackBar.open(
          'Password is too weak. Please strengthen it.',
          'OK',
          { duration: 5000 }
        );
        return;
      }

      // Proceed with signup if password is not breached and not weak
      this.proceedWithSignup();
    } else {
      // Find the first invalid control and go to its step
      const firstInvalidControl = Object.keys(this.signupForm.controls).find(key => this.signupForm.controls[key].invalid);
      if (firstInvalidControl) {
        if (firstInvalidControl === 'email') {
          this.step = 1;
        } else if (firstInvalidControl === 'fullName' || firstInvalidControl === 'password' || firstInvalidControl === 'termsAccepted') {
          this.step = 2;
        }
      }
    }
  }

  private proceedWithSignup() {
    this.isDisable = true;
      const attributionPayload = getStoredAttributionPayload(localStorage);
      
      const email = this.signupForm.value.email;
      this.recaptchaV3Service.execute("signup").subscribe((response_captcha: string) => {
        console.log("Signup on response captcha", response_captcha);
        let captchaDetails = {
          recaptcha_token: response_captcha,
          recaptcha_action: 'signup',
        }
        this
          ._projectService
          .doSignUp(this.signupForm.value, null, captchaDetails, attributionPayload)
          .subscribe((user: any) => {
            this.isDisable = false;
            // Store email for verification page
            this.tokenEncryptionService.setEncryptedItem('current_email', email);
            
            this._snackBar.open('Account created! Please verify your email with the OTP sent to your inbox.', 'OK', {
              duration: 4000
            });
            trackClarityEvent('signup_completed');
            
            // Redirect to OTP verification page
            this.router.navigate(["/verification-code"], { queryParams: { type: 'signup' } });
          }, err => {
            this.isDisable = false;
            if (err.error?.code === 'existing_user_email') {
              this.step = 1; // Go back to the email step
              setTimeout(() => {
                this.signupForm.get('email')?.setErrors({ emailExists: true });
                this.cdRef.detectChanges(); // Manually trigger change detection
              }, 1000);
            } else {
              this._snackBar.open(err.error.message, 'Undo', {
                duration: 3000
              });
            }
          });
      })
  }

  nextStep() {
    this.formSubmitted = true; // Mark as submitted to trigger validation messages
    if (this.step === 1) {
      const emailControl = this.signupForm.get('email');
      emailControl?.markAsTouched();
      if (emailControl?.valid) {
        this.step++;
      }
    }
  }

  onFormKeyDown(event: KeyboardEvent) {
    // Handle Enter key press in step 1 to prevent form submission
    if (event.key === 'Enter' && this.step === 1) {
      event.preventDefault();
      this.nextStep();
    }
  }

  prevStep() {
    if (this.step > 1) {
      this.step--;
    }
  }

  gotoSignIn() {
    this.router.navigate(["/login"]);
  }

  googleSigninAction() {
    this.loginService.GoogleAuth().then((result: any) => {
      // Assuming the GoogleAuth() method in LoginService returns a promise
      // that resolves with the Firebase auth result.
      const userProfile = result.additionalUserInfo?.profile;
      const userCredential = result.user;

      if (userProfile && userCredential) {
        // You might want to create a payload similar to your regular signup
        // or directly use the information to register/login the user via your backend.
        const googleUserPayload: any = {
          email: userProfile.email,
          firstName: userProfile.given_name,
          lastName: userProfile.family_name,
          isGoogleLogin: true,
          // You might need to handle company, contact, teamSize differently
          // or prompt the user for this information if it's a first-time signup.
          // For now, let's assume your backend can handle this or it's not strictly required for Google Sign-In flow immediately.
        };

        const attributionPayload = getStoredAttributionPayload(localStorage);
        if (attributionPayload) {
          const analyticsAttribs = getAnalyticsAttribsWithTouches(attributionPayload);
          googleUserPayload.first_touch = attributionPayload.first_touch;
          googleUserPayload.last_touch = attributionPayload.last_touch;
          if (analyticsAttribs) {
            googleUserPayload.meta = {
              ss_analytics_attribs: analyticsAttribs
            };
          }
        }

        // Example: Call a specific service method for Google Sign-Up/Login
        this._projectService.doSocialLogin(googleUserPayload).subscribe({
          next: (response: any) => {
            this.authService.handleSuccessfulLogin(response, true, true);
          },
          error: (err) => {
            this.isDisable = false;
            Swal.fire({
              icon: 'error',
              title: 'Google Sign-In Failed',
              text: err.error?.message || 'An unexpected error occurred.',
            });
          }
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Google Sign-In Failed',
          text: 'Could not retrieve user information from Google.',
        });
      }
    }).catch(error => {
      this.isDisable = false;
      Swal.fire({
        icon: 'error',
        title: 'Google Sign-In Error',
        text: error.message || 'An error occurred during Google Sign-In.',
      });
    });
  }

  handleGoogleSigninSuccess(event: any) {
    // This method will be called by the app-google-signin component on success.
    // The event payload should contain the necessary user information from Google.
    // We can adapt the logic from googleSigninAction or directly use the event data.

    // For now, let's assume the event itself is the response expected by handleSuccessfulLogin
    // or contains enough info to be processed by a backend service for social login.

    // If app-google-signin handles the direct Firebase interaction:
    // We might need to call a backend endpoint here to register/login the user
    // in our own system using the Google token or user info.

    // Let's assume `event` is the data similar to `check` in the original login flow
    // or what `_projectService.doSocialLogin` would return.
    // This part needs to align with what `app-google-signin` actually emits.

    // If app-google-signin component internally calls this.loginService.GoogleAuth()
    // and then emits a success event with the user data (e.g., from doSocialLogin response)
    // then we can directly use authService.handleSuccessfulLogin.

    // Placeholder: Assuming `event` is the fully processed user data ready for login state management
    // This is a common pattern if app-google-signin is a self-contained social login handler.
    if (event && event.token) { // Check if the event contains a token, similar to a regular login response
      this.authService.handleSuccessfulLogin(event, true, true);
    } else {
      // If the event structure is different, we might need to call googleSigninAction()
      // or process the `event` data to make it compatible.
      // For simplicity, if `event` is just a trigger, we can call our existing googleSigninAction.
      // However, `app-google-signin` likely provides user data.
      console.warn("handleGoogleSigninSuccess received an event without a token. Event data:", event);
      // As a fallback or if app-google-signin just signals to start the process:
      // this.googleSigninAction(); 
      // This line ^ might be redundant if app-google-signin already did the auth.
      // It's better if app-google-signin provides the auth result.

      // Let's refine this based on common practices for such components:
      // Often, a generic google-signin component would emit the raw `firebase.auth.UserCredential`
      // or a processed user object.

      // If `event` is `firebase.auth.UserCredential` from `signInWithPopup`:
      const userProfile = event.additionalUserInfo?.profile;
      const userCredential = event.user;

      if (userProfile && userCredential) {
        const googleUserPayload: any = {
          email: userProfile.email,
          firstName: userProfile.given_name,
          lastName: userProfile.family_name,
          isGoogleLogin: true,
          uid: userCredential.uid, // Firebase UID
          idToken: null // Placeholder, might need to get this via userCredential.getIdToken()
        };

        const attributionPayload = getStoredAttributionPayload(localStorage);
        if (attributionPayload) {
          const analyticsAttribs = getAnalyticsAttribsWithTouches(attributionPayload);
          googleUserPayload.first_touch = attributionPayload.first_touch;
          googleUserPayload.last_touch = attributionPayload.last_touch;
          if (analyticsAttribs) {
            googleUserPayload.meta = {
              ss_analytics_attribs: analyticsAttribs
            };
          }
        }

        userCredential.getIdToken().then(idToken => {
          googleUserPayload.idToken = idToken;
          this._projectService.doSocialLogin(googleUserPayload).subscribe({
            next: (response: any) => {
              this.authService.handleSuccessfulLogin(response, true, true);
            },
            error: (err) => {
              Swal.fire({
                icon: 'error',
                title: 'Google Sign-In Failed',
                text: err.error?.message || 'Failed to process Google Sign-In with backend.',
              });
            }
          });
        }).catch(tokenError => {
          Swal.fire({
            icon: 'error',
            title: 'Google Sign-In Token Error',
            text: 'Could not retrieve ID token from Google.',
          });
        });

      } else {
        Swal.fire({
          icon: 'error',
          title: 'Google Sign-In Failed',
          text: 'Invalid user data received from Google Sign-In component.',
        });
      }
    }
  }

  openSupersee() {
    window.open('Https://supersee.io', '_blank')
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
    this.cdRef.detectChanges(); // Manually trigger change detection
  }

  openTermsAndConditions(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    window.open('https://supersee.io/terms-conditions/', '_blank', 'noopener,noreferrer');
  }

  ngOnInit(): void {
    // Ensure form is created and reset explicitly on component initialization
    if (!this.signupForm) { // Defensive check, though constructor should create it
      this.createSignupForm();
    }
    this.signupForm.reset({
      fullName: '',
      email: '',
      password: '',
      termsAccepted: false
    });
    this.signupForm.markAsPristine();
    this.signupForm.markAsUntouched();
    this.formSubmitted = false; // Reset submission status
    this.submitAttempted = false;
    this.step = 1; // Ensure step is 1 on init
    // Reset password strength indicators
    this.passwordStrength = 'weak';
    this.passwordFeedback = [];
    this.passwordValidationErrors = [];
    this.passwordRequirements = [];
    this.unmetPasswordRequirements = [];
    this.unmetRequirementsCount = 0;
    this.metRequirementsCount = 0;
    this.requirementsProgress = 0;
    this.hasUnmetRequirements = false;
    this.hasMinLength = false;
    this.passwordVisible = false; // Reset password visibility
    this.isPasswordBreached = false;
    this.isCheckingBreach = false;
    if (this.breachCheckTimeout) {
      clearTimeout(this.breachCheckTimeout);
      this.breachCheckTimeout = null;
    }

    this.sharedService.sideMenuSubject.next(false);

    // Set initial value for the counter with some randomization
    this.currentUptimeHours = this.baseUptimeHours + Math.floor(Math.random() * 1000);

    // Start animations
    this.startUptimeCounter();
    this.startTestimonialRotation();

    const queryParams = this.route.snapshot.queryParamMap;
    this.icp = resolveIcpForVisit(queryParams, sessionStorage);
    // Tag the session so recordings and funnels can be filtered by the headline shown
    setClarityTag('signup_variant', this.icp);
    trackProductEvent('signup_viewed', { icp: this.icp });
    captureAttributionTouch(queryParams, localStorage, window.location.href, document.referrer);
  }

  initializeRandomCounter(): void {
    // Generate a random number between -1000 and +1000 to add to base value
    const randomOffset = Math.floor(Math.random() * 2000) - 1000;

    // Add current date influence for more variability
    const today = new Date();
    const dateInfluence = (today.getDate() + today.getMonth() * 30) * 5;

    // Set starting value with base + random offset + date influence
    this.currentUptimeHours = this.baseUptimeHours + randomOffset + dateInfluence;

    // Format with commas
    this.animatedHoursValue = this.formatNumberWithCommas(this.currentUptimeHours);
  }

  ngAfterViewInit(): void {
    this.setupVideoListener();
    if (this.heroVideo && this.heroVideo.nativeElement) {
      const videoElement = this.heroVideo.nativeElement;

      // Ensure video is muted to help with autoplay
      videoElement.muted = true;
      videoElement.defaultMuted = true;

      // Force load the video
      videoElement.load();

      // Handle video loading errors
      videoElement.onerror = (e) => {
        console.error('Video loading error:', e);
      };

      // Log when video is loaded
      videoElement.onloadeddata = () => {
        console.log('Video loaded successfully');
      };

      // Add event listeners for document interaction
      const attemptPlay = () => {
        // Attempt to play the video
        const playPromise = videoElement.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('Video playback started successfully');
              // Remove event listeners once video plays
              document.removeEventListener('click', attemptPlay);
              document.removeEventListener('touchstart', attemptPlay);
            })
            .catch(error => {
              console.error('Video playback was prevented:', error);
            });
        }
      };

      // Try initial play (will likely fail due to autoplay policy)
      attemptPlay();

      // Set up listeners to try again on user interaction
      document.addEventListener('click', attemptPlay);
      document.addEventListener('touchstart', attemptPlay);
    }

    const backgroundVideoElement = this.backgroundVideoRef.nativeElement;
    backgroundVideoElement.muted = true; // Ensure video is muted
    // Delay initial fade-in slightly after component init
    setTimeout(() => {
      // Removed fade-in logic
    }, 800); // Matches longest other animation delay

    backgroundVideoElement.play().catch(error => console.error('Video autoplay failed:', error));
  }

  ngOnDestroy(): void {
    // Unsubscribe from router events
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    // Clean up listener
    if (this.videoTimeUpdateListener && this.backgroundVideoRef) {
      this.backgroundVideoRef.nativeElement.removeEventListener('timeupdate', this.videoTimeUpdateListener);
    }
    // Clear the interval
    if (this.uptimeIntervalId) {
      clearInterval(this.uptimeIntervalId); // Clear uptime interval
    }
    if (this.testimonialInterval) { // Add this check and clear
      clearInterval(this.testimonialInterval);
    }
    // Clear breach check timeout
    if (this.breachCheckTimeout) {
      clearTimeout(this.breachCheckTimeout);
      this.breachCheckTimeout = null;
    }
  }

  setupVideoListener(): void {
    const video = this.backgroundVideoRef?.nativeElement;
    if (!video) {
      console.warn('Background video element not found.');
      return;
    }

    this.videoTimeUpdateListener = () => {
      const currentTime = video.currentTime;
      // Removed spotlight caption logic
    };

    video.addEventListener('timeupdate', this.videoTimeUpdateListener);
    // Trigger initial check in case video is already playing or at time 0
    setTimeout(() => this.videoTimeUpdateListener(), 50);
  }

  startUptimeCounter(): void {
    // Initial small random increment to make it look active right away
    setTimeout(() => {
      this.incrementCounter();
    }, 1500);

    // Set up regular interval with random increments
    this.uptimeIntervalId = setInterval(() => {
      this.incrementCounter();
    }, 3000); // Update every 3 seconds for more visible activity
  }

  incrementCounter(): void {
    // Add a random increment between 1-5 hours to make it look more realistic
    const increment = Math.floor(Math.random() * 5) + 1;
    this.currentUptimeHours += increment;

    // Format with commas to maintain consistent width
    const formattedValue = this.formatNumberWithCommas(this.currentUptimeHours);

    // Only update if the value has actually changed to avoid unnecessary animations
    if (this.animatedHoursValue !== formattedValue) {
      this.animatedHoursValue = formattedValue;
    }
  }

  formatNumberWithCommas(num: number): string {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // --- Testimonial Rotation --- 
  startTestimonialRotation(): void {
    const rotationTime = 6000; // Time each testimonial is shown (ms)
    const fadeTime = 500;    // Duration of fade transition (ms)

    // Initial delay before first fade-in
    setTimeout(() => {
      this.isTestimonialVisible = true;
      this.testimonialInterval = setInterval(() => {
        this.isTestimonialVisible = false; // Start fade out

        setTimeout(() => {
          this.currentTestimonialIndex = (this.currentTestimonialIndex + 1) % this.testimonials.length;
          this.currentTestimonial = this.testimonials[this.currentTestimonialIndex];
          this.isTestimonialVisible = true; // Start fade in
        }, fadeTime); // Wait for fade out

      }, rotationTime);
    }, 1000); // Delay before the very first testimonial shows

    // Start benefit rotation with Angular animations
    this.startBenefitRotation();
  }

  // --- Benefit Rotation with Angular Animations ---
  startBenefitRotation(): void {
    const rotationTime = 4000; // Time each benefit is shown (ms)
    const fadeTime = 500;    // Duration of fade transition (ms)

    // Initial state
    this.benefitAnimationState = 'in';
    this.currentBenefit = this.rotatingBenefits[0];

    // Set up interval for rotation
    setInterval(() => {
      // Trigger fade out
      this.benefitAnimationState = 'out';

      // After fade out completes, change text and fade in
      setTimeout(() => {
        this.currentBenefitIndex = (this.currentBenefitIndex + 1) % this.rotatingBenefits.length;
        this.currentBenefit = this.rotatingBenefits[this.currentBenefitIndex];
        this.benefitAnimationState = 'in';
      }, fadeTime);

    }, rotationTime);
  }
}
